import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const BRAND_GRADIENT = 'linear-gradient(135deg, #0047ff, #00c4ff)';

/* ================= WRAPPER ================= */

const Wrapper = styled.div`
  min-height: 100vh;
  font-family: 'Poppins', sans-serif;
  color: #e5e7eb;
  padding: 80px 16px;
  overflow-x: hidden;
`;

/* ================= HERO ================= */

const Hero = styled(motion.section)`
  text-align: center;
  margin-bottom: 60px;
`;

const Heading = styled.h1`
  font-size: 2.5rem;
  margin-bottom: 20px;
  background: ${BRAND_GRADIENT};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const Paragraph = styled.p`
  max-width: 850px;
  margin: 0 auto 30px;
  line-height: 1.6;
  font-size: 1.05rem;
  color: #cbd5e1;
`;

/* ================= GRID ================= */

const Grid = styled.div`
  width: 100%;
  max-width: 1280px;
  margin: 0 auto;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 24px;
  align-items: start;
  box-sizing: border-box;

  & > * {
    flex: 0 0 calc(25% - 18px);
    max-width: calc(25% - 18px);
    box-sizing: border-box;
  }

  @media (max-width: 1100px) {
    & > * {
      flex: 0 0 calc(50% - 12px);
      max-width: calc(50% - 12px);
    }
  }

  @media (max-width: 640px) {
    & > * {
      flex: 0 0 100%;
      max-width: 100%;
    }
  }
`;

/* ================= CARD ================= */

const Card = styled(motion.div)`
  width: 100%;
  background: rgba(13, 22, 45, 0.72);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 24px;
  padding: 28px;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
  box-sizing: border-box;
  box-shadow: 0 16px 36px rgba(0, 0, 0, 0.35);

  transition:
    border-color 0.35s ease,
    box-shadow 0.35s ease,
    transform 0.35s cubic-bezier(0.2, 0.8, 0.2, 1);

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(135deg, #0047ff, #00c4ff);
    opacity: 0.8;
  }

  &:hover {
    border-color: rgba(0, 196, 255, 0.45);
    box-shadow:
      0 22px 50px rgba(0, 71, 255, 0.25),
      0 0 25px rgba(0, 196, 255, 0.2);
    transform: translateY(-8px);
  }
`;

/* ================= CARD CONTENT ================= */

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
`;

/* ================= QUOTE ================= */

const Quote = styled.p`
  font-size: 0.92rem;
  line-height: 1.65;
  margin: 0;
  color: #94a3b8;

  ${({ $expanded }) =>
    !$expanded &&
    `
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 6;
      overflow: hidden;
      text-overflow: ellipsis;
    `}
`;

/* ================= READ MORE BUTTON ================= */

const ReadMoreButton = styled.button`
  margin-top: 14px;
  width: fit-content;
  padding: 8px 16px;
  border-radius: 10px;
  border: 1px solid rgba(0, 196, 255, 0.25);
  background: rgba(0, 196, 255, 0.08);
  color: #38bdf8;
  font-family: inherit;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;

  transition:
    background 0.25s ease,
    border-color 0.25s ease,
    color 0.25s ease;

  &:hover {
    background: rgba(0, 196, 255, 0.15);
    border-color: rgba(0, 196, 255, 0.5);
    color: #ffffff;
  }
`;

/* ================= AUTHOR ================= */

const Author = styled.p`
  font-size: 0.88rem;
  font-weight: 600;
  margin: 24px 0 0;
  background: ${BRAND_GRADIENT};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

/* ================= TESTIMONIALS DATA ================= */

const TESTIMONIALS = [
  {
    id: 'parinay',
    quote: `“Berrybloomer transformed our vision into reality with Parinay Sanskar, an advanced AI matrimony app designed for serious matchmaking. From intelligent filter-based search to a smooth, fast, and beautifully designed interface, the app stands out in both performance and usability. The attention to detail and user-centric approach makes Parinay Sanskar a truly premium matrimony platform. We highly recommend Berrybloomer for scalable and modern app development.”`,
    author: '– Parinay Sanskar',
  },
  {
    id: 'amara',
    quote: `“Berrybloomer delivered an exceptional experience while building Amara, our global luxury dating app. The app combines intelligent matchmaking, refined filters, and an elegant design to create a seamless and premium dating experience.”`,
    author: '– Amara',
  },
  {
    id: 'mypet',
    quote: `“Berrybloomer developed MyPet, a powerful and intuitive pet care platform that simplifies pet management for users. From smart health tracking to seamless appointment scheduling and service integration, the app delivers a smooth and user-friendly experience. The clean design and performance optimization make MyPet a reliable solution for modern pet owners.”`,
    author: '– MyPet',
  },
  {
    id: 'homehelp',
    quote: `“Berrybloomer built HomeHelp as a smart and efficient home service platform connecting users with trusted maids and helpers. With real-time booking, verified profiles, and seamless service management, the app ensures convenience and reliability. The intuitive interface and fast performance make HomeHelp an excellent solution for everyday household needs.”`,
    author: '– HomeHelp',
  },
  {
    id: 'zipzapcart',
    quote: `"Berrybloomer delivered ZipZapCart with exceptional speed and precision — a hyperlocal grocery delivery app built for the fast-paced demands of modern consumers. The real-time courier tracking, instant order processing, and seamless digital payment integration work flawlessly together. The intuitive UI and rock-solid performance have made ZipZapCart a go-to solution for quick and reliable grocery delivery. Berrybloomer's team showed outstanding technical depth and commitment throughout the entire development journey."`,
    author: '– ZipZapCart',
  },
  {
    id: 'mockstation',
    quote: `"Berrybloomer built Mock Station as a comprehensive and student-friendly exam preparation platform. The app delivers date and year-wise mock tests, latest exam pattern-based questions, instant result analysis, and a convenient answer key checker — all in one place. The clean interface and blazing-fast performance have made Mock Station an indispensable tool for competitive exam aspirants. We are thoroughly impressed by Berrybloomer's ability to translate complex requirements into a seamless product."`,
    author: '– Mock Station',
  },
  {
    id: 'econari',
    quote: `"Berrybloomer crafted a powerful digital presence for ECO Nari Foundation, our social impact platform dedicated to women empowerment and community development. The website effectively communicates our mission around environmental awareness and social welfare initiatives, reaching a wider audience with clarity and purpose. The thoughtful design, smooth user experience, and meaningful content structure reflect Berrybloomer's deep understanding of purpose-driven digital platforms. We are grateful for their passion and professionalism."`,
    author: '– ECO Nari Foundation',
  },
  {
    id: 'ashtroshine',
    quote: `"Berrybloomer brought Ashtroshine to life as a comprehensive astrology and spiritual consultation platform. From personalized horoscope analysis and live astrologer consultations to kundli matching and daily spiritual guidance, every feature was executed with exceptional care and quality. The elegant design and smooth performance create a truly premium experience for our users seeking spiritual clarity. Berrybloomer has been an outstanding technology partner for our vision."`,
    author: '– Ashtroshine',
  },
  {
    id: 'destinyachievers',
    quote: `"Berrybloomer delivered an outstanding digital platform for Destiny Achievers - our numerology and spiritual guidance website. From personal and business numerology consultations to spiritual product listings and life-path insights, every section was crafted with purpose and precision. The website's elegant design, intuitive layout, and smooth performance have helped us connect with over 40,000 seekers who trust us to decode their destiny through numbers. Berrybloomer truly understood our vision of blending spirituality with technology, and delivered a product that reflects it beautifully. Where numbers speak, destiny answers - and Berrybloomer made that possible."`,
    author: '– Destiny Achievers',
  },
];

/* ================= INDIVIDUAL CARD COMPONENT ================= */

function TestimonialCard({ item }) {
  const [expanded, setExpanded] = useState(false);
  const [canOverflow, setCanOverflow] = useState(false);
  const quoteRef = useRef(null);

  useEffect(() => {
    const checkOverflow = () => {
      const el = quoteRef.current;
      if (!el) return;
      if (!expanded) {
        setCanOverflow(el.scrollHeight > el.clientHeight + 2);
      }
    };

    checkOverflow();

    window.addEventListener('resize', checkOverflow);

    if (document.fonts) {
      document.fonts.ready.then(checkOverflow);
    }

    return () => window.removeEventListener('resize', checkOverflow);
  }, [expanded, item.quote]);

  return (
    <Card>
      <CardContent>
        <Quote ref={quoteRef} $expanded={expanded}>
          {item.quote}
        </Quote>

        {canOverflow && (
          <ReadMoreButton onClick={() => setExpanded((prev) => !prev)}>
            {expanded ? 'Read Less' : 'Read More'}
          </ReadMoreButton>
        )}

        <Author>{item.author}</Author>
      </CardContent>
    </Card>
  );
}

/* ================= MAIN TESTIMONIALS COMPONENT ================= */

export default function Testimonials() {
  return (
    <Wrapper>
      <Hero
        initial={{ opacity: 0, y: -30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        <Heading>What Our Clients Say</Heading>
        <Paragraph>
          Discover how we've helped our clients achieve remarkable success
          through innovative solutions and expert collaboration.
        </Paragraph>
      </Hero>

      <Grid>
        {TESTIMONIALS.map((item) => (
          <TestimonialCard key={item.id} item={item} />
        ))}
      </Grid>
    </Wrapper>
  );
}
